import cn from 'classnames'
import { useCallback, useEffect, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { LoaderOverlay } from 'core-components/library/loader'
import { IModalContent } from 'core-components/library/modalDialog'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { applyPlanningSmb } from '../../../../api'
import { PLANNING_SMB_TRANSLATION_KEYS } from '../../../../configs'
import { usePlanningSmbEditor } from '../../../../hooks/usePlanningSmbEditor'
import {
  IPlanningSmb,
  ISmbNode,
  TGetSmbFinancialValue,
  TSmbSplitResult,
} from '../../../../model/types'
import { PlanningSmbControls } from '../planningSmbControls/PlanningSmbControls'
import { PlanningSmbGrid } from '../planningSmbGrid/PlanningSmbGrid'

import styles from './PlanningSmbContent.module.scss'

const KEYS = PLANNING_SMB_TRANSLATION_KEYS

export interface IPlanningSmbContentProps {
  tree: ISmbNode
  planningSmb: IPlanningSmb[]
  partnerId: string
  fYear: number
  lastPlanYear?: number
  programType: string
  /** Direct Category node to scroll to and briefly highlight on open. */
  focusNodeId: string
  planActualsType: ActualsValueTypes
  planCurrency: string
  /** Option lists taken from Planning, so the popup shows the same choices */
  actualsTypes: ActualsValueTypes[]
  currencies: string[]
  /** Current number scale from Planning; changing it here updates that same Planning state. */
  roundingValue: string
  roundingOptions: string[]
  onRoundingChange: (value: string) => void
  /** Initial value of the Quarterly Planning switch from the category planning page. */
  isQuarterlyPlanning: boolean
  /** Mirrors the category switch disabled state (`isOnlyQuarterlyPlanning`). */
  isQuarterlyPlanningDisabled: boolean
  getFinancialValue: TGetSmbFinancialValue
  formatMoney: (value: number) => string
  /** Reloads the whole planning data set once the split has been saved */
  onSaved: () => void | Promise<void>
  onSaveFailed: (error: unknown) => void
  /** Opens the Offering-level guided alternative without changing classic-editor behavior. */
  onSwitchGuided?: () => void
  /** Keeps every modal close path locked while save and the following reload are pending. */
  onSavingChange?: (isSaving: boolean) => void
  /** Keeps the Offering status tile in the modal header live while values are edited. */
  onSummaryChange?: (summary: TSmbSplitResult) => void
}

export const PlanningSmbContent = ({
  tree,
  planningSmb,
  partnerId,
  fYear,
  lastPlanYear,
  programType,
  focusNodeId,
  planActualsType,
  planCurrency,
  actualsTypes,
  currencies,
  roundingValue,
  roundingOptions,
  onRoundingChange,
  isQuarterlyPlanning,
  isQuarterlyPlanningDisabled,
  getFinancialValue,
  formatMoney,
  onSaved,
  onSaveFailed,
  onSwitchGuided,
  onSavingChange,
  onSummaryChange,
  closeModal,
}: IPlanningSmbContentProps & IModalContent) => {
  const t = useSafetyTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const [showFinancialData, setShowFinancialData] = useState(false)

  const editor = usePlanningSmbEditor({
    tree,
    planningSmb,
    partnerId,
    fYear,
    lastPlanYear,
    programType,
    planActualsType,
    planCurrency,
    initialIsQuarterly: isQuarterlyPlanning,
    getFinancialValue,
  })

  const { summary, isDirty, getRowsToSave } = editor

  useEffect(() => onSummaryChange?.(summary), [onSummaryChange, summary])

  const handleSave = useCallback(async () => {
    // Nothing was edited - closing is the same as cancelling, no round trip and no reload.
    if (!isDirty) {
      closeModal()

      return
    }

    setIsSaving(true)
    onSavingChange?.(true)
    try {
      await applyPlanningSmb({ partnerId, fYear, rows: getRowsToSave() })
      await onSaved()
      setIsSaving(false)
      onSavingChange?.(false)
      closeModal()
    } catch (error) {
      setIsSaving(false)
      onSavingChange?.(false)
      onSaveFailed(error)
    }
  }, [isDirty, closeModal, partnerId, fYear, getRowsToSave, onSaved, onSaveFailed, onSavingChange])

  return (
    <div
      className={cn(styles.content, { [styles.contentAnnual]: !editor.isQuarterly })}
      aria-busy={isSaving}
    >
      <PlanningSmbControls
        isQuarterly={editor.isQuarterly}
        isQuarterlyDisabled={isQuarterlyPlanningDisabled}
        showFinancialData={showFinancialData}
        baselineMetric={editor.baselineMetric}
        baselineCurrency={editor.baselineCurrency}
        actualsTypes={actualsTypes}
        currencies={currencies}
        roundingValue={roundingValue}
        roundingOptions={roundingOptions}
        disabled={isSaving}
        onQuarterlyChange={editor.setIsQuarterly}
        onShowFinancialDataChange={setShowFinancialData}
        onBaselineMetricChange={editor.setBaselineMetric}
        onBaselineCurrencyChange={editor.setBaselineCurrency}
        onRoundingChange={onRoundingChange}
      />

      <div className={styles.body}>
        <PlanningSmbGrid
          rows={editor.rows}
          columns={editor.columns}
          focusNodeId={focusNodeId}
          isQuarterly={editor.isQuarterly}
          showFinancialData={showFinancialData}
          fYear={fYear}
          disabled={isSaving}
          getWeight={editor.getWeight}
          getBaselineWeight={editor.getBaselineWeight}
          getCellValue={editor.getCellValue}
          getBaselineValue={editor.getBaselineValue}
          formatMoney={formatMoney}
          onToggleExpanded={editor.toggleExpanded}
          onCommit={editor.commitEdit}
        />
      </div>

      <div className={styles.foot}>
        <div className={styles.footActions}>
          {onSwitchGuided && (
            <Button
              color="primary"
              variant="contained"
              disabled={isSaving}
              onClick={onSwitchGuided}
            >
              <Translation span text={KEYS.modal.tryGuidedSetup} />
            </Button>
          )}
          <Button variant="outlined" color="grey" disabled={isSaving} onClick={closeModal}>
            <Translation span text={KEYS.modal.cancel} />
          </Button>
          {/* Save stays out of reach until there is something to save, so the editor itself
              shows whether anything was changed */}
          <Button
            color="primary"
            disabled={isSaving || !isDirty}
            loading={isSaving}
            onClick={handleSave}
          >
            <Translation span text={isSaving ? KEYS.modal.saving : KEYS.modal.save} />
          </Button>
        </div>
      </div>

      <LoaderOverlay className={styles.savingOverlay} show={isSaving} />

      {/* Overriding a coarser row discards the finer grained values already entered below it */}
      {!!editor.pendingEdit && (
        <ConfirmationDialog
          isVisible
          title={KEYS.confirm.title}
          confirmBtnText={KEYS.confirm.apply}
          rejectBtnText={KEYS.confirm.cancel}
          onConfirm={editor.confirmPendingEdit}
          onReject={editor.cancelPendingEdit}
          content={
            <Translation
              text={KEYS.confirm.body}
              replaceParams={[
                // The edited level: a Category, Sub-category, L3 - or the Offering itself
                { key: '{category}', value: t(editor.pendingEdit.nodeNameKey) },
                { key: '{fYear}', value: `${editor.pendingEdit.year}`.slice(-2) },
              ]}
            />
          }
        />
      )}
    </div>
  )
}
